import { useSearchParams } from "react-router-dom";
import { OnboardingLayout } from "onboarding/OnboardingLayout.tsx";
import { OnboardingContextProvider } from "onboarding/OnboardingContext.tsx";
import { PostHogProvider } from "posthog-js/react";
import { StyleRoot } from "@ttc3k/trekker";
import { ApolloProviderPublic } from "shared/apollo";
import { OnboardingFlow } from "./OnboardingFlow";
import { EnumEntityReferenceType } from "gql/generated.ts";
import { PostHogAppContext, usePostHog } from "shared/hooks/usePostHog";

export const Onboarding = () => {
  const { posthog, initPostHog, registerPostHog } = usePostHog();

  // Initialize PostHog
  initPostHog("whereabouts-next-onboarding");

  // Register the appContext property
  registerPostHog({
    appContext: PostHogAppContext.WhereaboutsNext, // This ensures all events from this instance are tagged with `appContext: "whereabouts-next"`
  });

  const [searchParams] = useSearchParams();

  /* Search param for organization id (when an org invites a user OR cold signup via org website) */
  const organizationId = searchParams.get("organizationId");

  /* Search param to check if user is being invited from an operator or organization */
  const invitedFrom = searchParams.get(
    "invitedFrom",
  ) as EnumEntityReferenceType;

  /* Search param for operatorId and inviterEmail (when an operator invites a user to their business) */
  const operatorName = decodeURIComponent(
    searchParams.get("operatorName") ?? "",
  );
  const operatorId = searchParams.get("operatorId");
  const inviterEmail = decodeURIComponent(
    searchParams.get("inviterEmail") ?? "",
  );

  /**
   * Search param to check if user was invited by organization
   * If true, show organization invited screen
   * If false, show cold signup screen
   */
  const invitedSearchParams = searchParams.get("invited");
  const isInvited: boolean = invitedSearchParams
    ? JSON.parse(invitedSearchParams)
    : false;

  /**
   * Search param to check if we should skip part of the onboarding flow for organizations
   * If true, skip screen with video and welcome message
   * If false, show screen with video and welcome message
   */
  const skipSearchParams = searchParams.get("skip");
  const skip: boolean = skipSearchParams ? JSON.parse(skipSearchParams) : false;

  /**
   * Search param to check inviting email
   * If true, input is disabled
   * If false, input is enabled
   */
  const invitedEmailAddress = decodeURIComponent(
    searchParams.get("email") ?? "",
  );

  const ctxHeaders = {
    authorization: "Bearer wbts-onboarding-user-app",
    metadata: JSON.stringify({
      // Set the metadata up depending on how we're accessing the onboarding flow
      type: organizationId
        ? "ORGANIZATION"
        : operatorName
          ? "OPERATOR"
          : "WHEREABOUTS",
      entityId: organizationId
        ? organizationId
        : operatorId
          ? operatorId
          : "ICE-COLD-SIGNUP",
    }),
    "wa-type": "onboarding",
  };

  return (
    <ApolloProviderPublic ctxHeaders={ctxHeaders}>
      <PostHogProvider client={posthog}>
        <StyleRoot>
          <OnboardingContextProvider
            organizationId={organizationId}
            invitedFrom={invitedFrom}
            operatorId={operatorId}
            operatorName={operatorName}
            skip={skip}
            inviterEmail={inviterEmail}
            isInvited={isInvited}
            emailAddress={invitedEmailAddress}
          >
            <OnboardingLayout>
              <OnboardingFlow />
            </OnboardingLayout>
          </OnboardingContextProvider>
        </StyleRoot>
      </PostHogProvider>
    </ApolloProviderPublic>
  );
};
